import { useEffect, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import API from '../../services/api';
import { applyLayout } from './layoutUtils';

// Loads the user's saved dashboard layout, exposes move/hide/show/reset,
// and saves changes back debounced + best-effort (failures are silent;
// the local layout still applies for the session).
// `registryIds` and `lockedIds` must be stable arrays (module-level or
// memoized). Locked ids are pinned first, can't be hidden, and any saved
// layout that says otherwise is overridden (see applyLayout).
export default function useDashboardLayout(registryIds, lockedIds = []) {
  const [layout, setLayout] = useState(() => applyLayout(registryIds, null, lockedIds));
  const saveTimer = useRef(null);
  const pendingSave = useRef(null);

  useEffect(() => {
    let active = true;
    API.get('/therapist/dashboard-layout')
      .then((res) => {
        if (active) setLayout(applyLayout(registryIds, res.data && res.data.layout, lockedIds));
      })
      .catch(() => { /* non-fatal: keep default layout */ });
    return () => {
      active = false;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        if (pendingSave.current) {
          API.put('/therapist/dashboard-layout', { layout: pendingSave.current }).catch(() => { /* best-effort */ });
        }
      }
    };
  }, []);

  const persist = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    pendingSave.current = next;
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      pendingSave.current = null;
      API.put('/therapist/dashboard-layout', { layout: next }).catch(() => { /* best-effort */ });
    }, 800);
  };

  const update = (fn) => {
    setLayout((prev) => {
      const next = fn(prev);
      if (next === prev) return prev;
      persist(next);
      return next;
    });
  };

  // Re-pin locked ids to the front after a reorder (a drop at index 0 lands
  // just after the locked block instead).
  const pin = (order) => [
    ...lockedIds.filter((id) => order.includes(id)),
    ...order.filter((id) => !lockedIds.includes(id)),
  ];

  const move = (activeId, overId) =>
    update((prev) => {
      if (lockedIds.includes(activeId)) return prev;
      const from = prev.order.indexOf(activeId);
      const to = prev.order.indexOf(overId);
      if (from < 0 || to < 0 || from === to) return prev;
      return { ...prev, order: pin(arrayMove(prev.order, from, to)) };
    });

  const hide = (id) =>
    update((prev) => {
      if (lockedIds.includes(id) || prev.hidden.includes(id)) return prev;
      return { ...prev, hidden: [...prev.hidden, id] };
    });

  const show = (id) =>
    update((prev) => ({ ...prev, hidden: prev.hidden.filter((h) => h !== id) }));

  const reset = () => update(() => applyLayout(registryIds, null, lockedIds));

  return { layout, move, hide, show, reset };
}
